"use client";

import React, { useState } from "react";
import { Link } from "@/components/common/Link";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { X, ArrowRight, Maximize2 } from "lucide-react";
const projects = [{
  id: 1,
  title: "Wild Garden Weddings",
  category: "Floral Design",
  description: "Bespoke bridal arrangements that capture the untamed beauty of our on-site garden. We specialize in organic, flowing silhouettes that feel as though they were gathered just moments before the ceremony.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675409/img/wedding_floral_design.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675409/img/wedding_floral_design.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675409/img/wedding_floral_design.png-wvc-sizes",
  imageAlt: "Wild Garden Weddings floral arrangement"
}, {
  id: 2,
  title: "Corporate Botanical Styling",
  category: "Event Decor",
  description: "Elevating professional environments with sophisticated greenery and architectural floral installations. Our corporate designs focus on clean lines and sustainable, long-lasting botanical elements.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675409/img/corporate_botanical_styling.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675409/img/corporate_botanical_styling.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675409/img/corporate_botanical_styling.png-wvc-sizes",
  imageAlt: "Corporate botanical styling for events"
}, {
  id: 3,
  title: "Private Garden Soirées",
  category: "Atmosphere",
  description: "Transforming intimate gatherings into immersive sensory experiences. From delicate table settings to overhead floral canopies, we bring the serenity of the PETAL garden to your private celebration.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675409/img/private_garden_soiree.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675409/img/private_garden_soiree.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675409/img/private_garden_soiree.png-wvc-sizes",
  imageAlt: "Private garden soirée floral decor"
}, {
  id: 4,
  title: "Seasonal Harvest Galas",
  category: "Artisanal Curation",
  description: "A celebration of the current bloom cycle. These large-scale events feature the very best of our seasonal harvest, showcasing the diverse textures and colors unique to each time of year.",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675409/img/seasonal_harvest_gala.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675409/img/seasonal_harvest_gala.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1675409/img/seasonal_harvest_gala.png-wvc-sizes",
  imageAlt: "Seasonal harvest gala floral installation"
}];
type Project = typeof projects[0];
export default function CuratedMasterpieces() {
  const [hoveredImg, setHoveredImg] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);
  return <section data-section-id="242994" id="event-portfolio" className="relative bg-background text-foreground min-h-screen py-24 overflow-hidden">
      {}
      <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none bg-background">
        {projects.map((p, i) => <img key={`bg-${p.id}`} data-index={i} src={p.imageSrc} data-wvc-srcset={p.imageSrcset} data-wvc-sizes={p.imageSizes} alt="" className={`absolute inset-0 w-full h-full object-cover blur-[120px] scale-125 transition-opacity duration-1000 ease-in-out ${hoveredImg === p.imageSrc ? "opacity-30" : "opacity-0"}`} />)}
        {}
        <div className="absolute inset-0 bg-gradient-to-b from-background/90 via-background/60 to-background/95" />
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-8">
        {}
        <div className="mb-20 max-w-3xl">
          <h2 className="text-4xl md:text-6xl font-serif font-bold mb-6 tracking-tight text-primary">
            Event Portfolio
          </h2>
          <p className="text-muted-foreground text-lg md:text-xl leading-relaxed font-default">
            Explore our curated collection of botanical experiences. From intimate garden weddings to grand corporate galas, we bring the artistry of PETAL to every occasion.
          </p>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {projects.map((project, index) => <div key={project.id} data-index={index} onMouseEnter={() => setHoveredImg(project.imageSrc)} onMouseLeave={() => setHoveredImg(null)} onClick={() => setSelectedProject(project)} className="group cursor-pointer">
              <Card className="h-[500px] lg:h-[650px] overflow-hidden border-none bg-transparent rounded-md relative shadow-md transition-shadow hover:shadow-xl">
                {}
                <div className="absolute inset-0 overflow-hidden bg-muted">
                  <img src={project.imageSrc} data-wvc-srcset={project.imageSrcset} data-wvc-sizes={project.imageSizes} alt={project.imageAlt} className="w-full h-full object-cover transition-transform duration-1000 ease-out group-hover:scale-105" />
                  {}
                  <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent opacity-40 group-hover:opacity-60 transition-opacity duration-500" />
                </div>

                {}
                <div className="absolute top-6 right-6 opacity-0 -translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500 ease-out z-20">
                  <div className="bg-background/40 backdrop-blur-md p-3 rounded-md text-primary">
                    <Maximize2 className="w-5 h-5" />
                  </div>
                </div>

                {}
                <div className="absolute bottom-0 left-0 right-0 p-8 translate-y-4 group-hover:translate-y-0 transition-transform duration-500 ease-out z-20">
                  <p className="text-secondary-foreground text-xs md:text-sm font-default mb-3 tracking-widest uppercase">
                    {project.category}
                  </p>
                  <h3 className="text-2xl md:text-3xl font-serif font-bold text-background leading-tight">
                    {project.title}
                  </h3>
                </div>
              </Card>
            </div>)}
        </div>
      </div>

      {}
      {selectedProject && <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/95 backdrop-blur-xl p-4 md:p-8 animate-in fade-in zoom-in-95 duration-300">
          <Button variant="ghost" size="icon" className="absolute top-4 right-4 md:top-8 md:right-8 z-50 rounded-md bg-secondary/50 hover:bg-secondary text-secondary-foreground" onClick={() => setSelectedProject(null)}>
            <X className="w-6 h-6" />
            <span className="sr-only">Close</span>
          </Button>

          <div className="w-full max-w-7xl h-full max-h-[90vh] flex flex-col lg:flex-row gap-0 overflow-hidden rounded-md bg-card border border-border shadow-2xl">
            {}
            <div className="w-full lg:w-1/2 h-[40vh] lg:h-full relative bg-muted">
              <img src={selectedProject.imageSrc} data-wvc-srcset={selectedProject.imageSrcset} data-wvc-sizes={selectedProject.imageSizes} alt={selectedProject.imageAlt} className="w-full h-full object-cover" />
            </div>

            {}
            <div className="w-full lg:w-1/2 p-8 md:p-16 flex flex-col justify-center overflow-y-auto">
              <p className="text-primary font-default text-sm tracking-widest uppercase mb-4">
                {selectedProject.category}
              </p>
              <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold mb-8 text-card-foreground">
                {selectedProject.title}
              </h2>
              <p className="text-muted-foreground text-lg md:text-xl mb-12 leading-relaxed font-default">
                {selectedProject.description}
              </p>
              
              <div className="mt-auto pt-8 border-t border-border">
                <Button asChild size="lg" className="rounded-md w-full sm:w-auto group bg-accent text-accent-foreground hover:bg-primary hover:text-primary-foreground transition-colors">
                  <Link to="/EventServicesPage">
                    View Event Services 
                    <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>}
    </section>;
}